
import React, { useState, useEffect, useMemo } from 'react';
import { Plus, X, Edit3, Trash2, Receipt, Calendar, DollarSign, User, FileText, Building, CreditCard } from 'lucide-react';
import { database, auth } from '../firebase';
import { ref, onValue, push, set, remove, update, serverTimestamp, get, off } from 'firebase/database';
import { onAuthStateChanged } from 'firebase/auth';

const encodeEmailForPath = (email) => {
  return email.replace(/[.#$[\]]/g, '_');
};

const ReceiptManagementSystem = ({ initialCustomer, onBack }) => {
  const [receipts, setReceipts] = useState([]);
  const [showAddForm, setShowAddForm] = useState(!!initialCustomer);
  const [editingReceipt, setEditingReceipt] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [banks, setBanks] = useState([]);
  const [customerSales, setCustomerSales] = useState([]);
  const [formData, setFormData] = useState({
    voucherNo: '',
    voucherDate: new Date().toISOString().split('T')[0],
    customer: initialCustomer?.customerName || '',
    customerAddress: initialCustomer?.address || '',
    customerCity: initialCustomer?.city || '',
    receiptType: '',
    receiptBy: '',
    bankName: '',
    chequeNo: '',
    chequeDate: '',
    remarks: '',
    transactions: [],
    totalAmount: '',
    netAmount: '',
    receiptAmount: ''
  });
  const [originalNetTotal, setOriginalNetTotal] = useState(0);
  const [currentOutstanding, setCurrentOutstanding] = useState(0);
  const [receiptAmountInput, setReceiptAmountInput] = useState('');
  const [paymentModes, setPaymentModes] = useState([]);
  const [isAllMR, setIsAllMR] = useState(false);
  const [selectedBill, setSelectedBill] = useState('');

  const filteredCustomerSales = useMemo(() => {
    if (!formData.customer) return [];
    return customerSales.filter(sale => sale.customer?.trim() === formData.customer.trim());
  }, [customerSales, formData.customer]);

  // Fetch payment modes
  useEffect(() => {
    if (currentUser) {
      const encodedEmail = encodeEmailForPath(currentUser.email);
      const paymentModesRef = ref(database, `users/${encodedEmail}/paymentModes`);
      const paymentModesListener = onValue(
        paymentModesRef,
        (snapshot) => {
          const data = snapshot.val();
          const modesList = data
            ? Object.keys(data).map((key) => ({
                id: key,
                code: data[key].code,
                description: data[key].description,
              }))
            : [];
          setPaymentModes(modesList);
        },
        (error) => {
          console.error('Error fetching payment modes:', error);
          alert('Failed to fetch payment modes.');
        }
      );
      return () => off(paymentModesRef, 'value', paymentModesListener);
    }
  }, [currentUser]);

  // Consolidated styles matching PayableTable
  const styles = {
    container: {
      maxWidth: '100%',
      margin: '0 auto',
      padding: '2rem',
      minHeight: '100vh',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    },
    formWrapper: {
      backgroundColor: '#ffffff',
      borderRadius: '12px',
      padding: '1.5rem',
      border: '1px solid #e2e8f0',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
      margin: '0 auto',
      maxWidth: '1200px',
      overflowX: 'auto',
    },
    formWrapper1: {
      borderRadius: '12px',
      padding: '1.5rem',
      margin: '0 auto',
      maxWidth: '1200px',
      overflowX: 'auto',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '2rem',
      padding: '1.5rem',
      backgroundColor: 'white',
      borderRadius: '6px',
      border: '1px solid #d1d5db',
      fontWeight: '600',
      color: '#374151',
      flexWrap: 'wrap',
      gap: '1rem',
    },
    headerTitle: {
      fontSize: '2rem',
      fontWeight: '700',
      color: '#2563eb',
      margin: '0',
    },
    headerButtons: {
      display: 'flex',
      gap: '0.5rem',
      flexWrap: 'wrap',
      alignItems: 'center',
    },
    backButton: {
      padding: '0.5rem 1rem',
      borderRadius: '6px',
      backgroundColor: '#6b7280',
      color: 'white',
      fontWeight: '500',
      cursor: 'pointer',
      border: 'none',
      fontSize: '0.8rem',
      transition: 'all 0.2s ease',
      boxShadow: '0 2px 8px rgba(107, 114, 128, 0.3)',
      transform: 'translateY(0)',
    },
    addButton: {
      padding: '0.875rem 2rem',
      borderRadius: '8px',
      backgroundColor: '#2563eb',
      color: 'white',
      fontWeight: '600',
      cursor: 'pointer',
      border: 'none',
      fontSize: '0.95rem',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 15px rgba(37, 99, 235, 0.3)',
      transform: 'translateY(0)',
    },
    section: {
      backgroundColor: '#fff',
      padding: '1.25rem',
      borderRadius: '8px',
      border: '1px solid #e2e8f0',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
      marginBottom: '1rem',
    },
    sectionHeading: {
      fontSize: '1.1rem',
      fontWeight: '600',
      marginBottom: '1rem',
      color: '#1f2937',
    },
    gridContainer: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
      gap: '1rem',
    },
    formGroup: {
      display: 'flex',
      flexDirection: 'column',
      marginBottom: '0.75rem',
    },
    label: {
      display: 'block',
      fontSize: '1rem',
      fontWeight: '500',
      color: '#374151',
      marginBottom: '0.25rem',
    },
    input: {
      display: 'block',
      width: '80%',
      padding: '0.5rem 0.75rem',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '0.8rem',
      backgroundColor: '#fff',
      transition: 'border-color 0.2s ease',
    },
    select: {
      display: 'block',
      width: '100%',
      padding: '0.5rem 0.75rem',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '0.8rem',
      backgroundColor: '#fff',
      cursor: 'pointer',
      appearance: 'none',
      backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
      backgroundPosition: 'right 0.5rem center',
      backgroundRepeat: 'no-repeat',
      backgroundSize: '1em 1em',
      paddingRight: '2rem',
    },
    textarea: {
      border: '1px solid #d1d5db',
      padding: '0.5rem 0.75rem',
      borderRadius: '6px',
      fontSize: '0.8rem',
      width: '80%',
      height: '60px',
      resize: 'none',
      backgroundColor: '#fff',
      transition: 'border-color 0.2s ease',
    },
    tableContainer: {
      marginTop: '1rem',
      overflowX: 'auto',
      backgroundColor: 'white',
      borderRadius: '8px',
      border: '1px solid #e2e8f0',
      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: '0.9rem',
      minWidth: '1000px',
      border: '1px solid #d1d5db',
    },
    tableHeader: {
      padding: '1rem 0.75rem',
      textAlign: 'center',
      backgroundColor: '#2563eb',
      color: 'white',
      fontWeight: '600',
      fontSize: '0.85rem',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      border: '1px solid #1e40af',
    },
    tableCell: {
      padding: '1rem 0.75rem',
      border: '1px solid #d1d5db',
      color: '#374151',
      verticalAlign: 'middle',
      textAlign: 'center',
    },
    tableInput: {
      border: '1px solid #d1d5db',
      padding: '0.3rem 0.5rem',
      borderRadius: '4px',
      fontSize: '0.75rem',
      boxSizing: 'border-box',
      width: '100%',
      height: '28px',
      backgroundColor: '#fff',
      transition: 'border-color 0.2s ease',
    },
    summaryContainer: {
      marginTop: '1.5rem',
      width: '100%',
      maxWidth: '250px',
      marginLeft: 'auto',
    },
    summaryBox: {
      border: '1px solid #e2e8f0',
      padding: '1rem',
      borderRadius: '8px',
      backgroundColor: '#fff',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
    },
    summaryRow: {
      display: 'flex',
      justifyContent: 'space-between',
      marginBottom: '0.5rem',
      fontSize: '0.8rem',
      color: '#374151',
    },
    summaryInput: {
      border: '1px solid #d1d5db',
      padding: '0.4rem',
      width: '40%',
      borderRadius: '4px',
      fontSize: '0.8rem',
      textAlign: 'right',
      backgroundColor: '#f3f4f6',
    },
    summaryTotal: {
      display: 'flex',
      justifyContent: 'space-between',
      fontWeight: '600',
      borderTop: '1px solid #e2e8f0',
      paddingTop: '0.5rem',
      marginTop: '0.75rem',
      color: '#1f2937',
    },
    actionButtons: {
      marginTop: '1.5rem',
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '0.75rem',
      paddingTop: '0.75rem',
      borderTop: '1px solid #e2e8f0',
      flexWrap: 'wrap',
    },
    saveButton: {
      padding: '0.5rem 1.25rem',
      borderRadius: '6px',
      backgroundColor: '#10b981',
      color: 'white',
      fontWeight: '500',
      fontSize: '0.8rem',
      cursor: 'pointer',
      border: 'none',
      transition: 'all 0.2s ease',
      boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
      transform: 'translateY(0)',
    },
    cancelButton: {
      padding: '0.5rem 1.25rem',
      borderRadius: '6px',
      backgroundColor: '#ef4444',
      color: 'white',
      fontWeight: '500',
      fontSize: '0.8rem',
      cursor: 'pointer',
      border: 'none',
      transition: 'all 0.2s ease',
      boxShadow: '0 2px 8px rgba(220, 38, 38, 0.3)',
      transform: 'translateY(0)',
    },
    tableContainerList: {
      backgroundColor: 'white',
      borderRadius: '16px',
      overflow: 'hidden',
      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
      border: '1px solid #e2e8f0',
      overflowX: 'auto',
    },
    actionCell: {
      display: 'flex',
      gap: '0.5rem',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '0.5rem',
    },
    editButton: {
      padding: '0.5rem',
      borderRadius: '6px',
      backgroundColor: '#10b981',
      color: 'white',
      fontWeight: '500',
      cursor: 'pointer',
      border: 'none',
      fontSize: '0.8rem',
      transition: 'all 0.2s ease',
      boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
      transform: 'translateY(0)',
    },
    deleteButton: {
      padding: '0.5rem',
      borderRadius: '6px',
      backgroundColor: '#ef4444',
      color: 'white',
      fontWeight: '500',
      cursor: 'pointer',
      border: 'none',
      fontSize: '0.8rem',
      transition: 'all 0.2s ease',
      boxShadow: '0 2px 8px rgba(220, 38, 38, 0.3)',
      transform: 'translateY(0)',
    },
    noData: {
      textAlign: 'center',
      padding: '4rem 2rem',
      color: '#718096',
      fontSize: '1.1rem',
    },
    srNoCell: {
      fontWeight: '600',
      textAlign: 'center',
      width: '60px',
    },
    paymentStatusBadge: {
      padding: '0.25rem 0.75rem',
      borderRadius: '20px',
      fontSize: '0.75rem',
      fontWeight: '600',
      color: 'white',
      textAlign: 'center',
      display: 'inline-block',
      minWidth: '80px',
    },
  };

  // Format currency for display
  const formatCurrency = (amount) => {
    const num = Number(amount);
    if (isNaN(num)) return '0.00';
    return num.toFixed(2);
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  };

  // Get payment status color
  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'Complete':
        return '#10b981';
      case 'Partial':
        return '#f59e0b';
      default:
        return '#ef4444';
    }
  };

  // Get payment status text with emoji
  const getPaymentStatusText = (status) => {
    switch (status) {
      case 'Complete':
        return '✅ Complete';
      case 'Partial':
        return '🔄 Partial';
      default:
        return '⏳ Pending';
    }
  };

  // Fetch data from Firebase
  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      if (user) {
        setCurrentUser(user);
        const encodedEmail = encodeEmailForPath(user.email);
        const receiptsRef = ref(database, `users/${encodedEmail}/receipts`);
        const customersRef = ref(database, `users/${encodedEmail}/customers`);
        const banksRef = ref(database, `users/${encodedEmail}/banks`);
        const salesRef = ref(database, `users/${encodedEmail}/sales`);
        
        const receiptsListener = onValue(receiptsRef, (snapshot) => {
          const data = snapshot.val();
          setReceipts(data ? Object.keys(data).map(key => ({ id: key, ...data[key] })) : []);
        });
        
        const customersListener = onValue(customersRef, (snapshot) => {
          const data = snapshot.val();
          setCustomers(data ? Object.keys(data).map(key => ({ id: key, ...data[key] })) : []);
        });
        
        const banksListener = onValue(banksRef, (snapshot) => {
          const data = snapshot.val();
          setBanks(data ? Object.values(data) : []);
        });
        
        const salesListener = onValue(salesRef, (snapshot) => {
          const data = snapshot.val();
          const salesArray = data ? Object.keys(data).map(key => ({ id: key, ...data[key] })) : [];
          setCustomerSales(salesArray);
        });
        
        return () => {
          off(receiptsRef, 'value', receiptsListener);
          off(customersRef, 'value', customersListener);
          off(banksRef, 'value', banksListener);
          off(salesRef, 'value', salesListener);
          unsubscribeAuth();
        };
      } else {
        setCurrentUser(null);
        setReceipts([]);
        setCustomers([]);
        setBanks([]);
        setCustomerSales([]);
        return () => unsubscribeAuth();
      }
    });
    return () => unsubscribeAuth();
  }, []);

  // Initialize form data for editing or new receipt
  useEffect(() => {
    if (editingReceipt) {
      const receiptAmountNum = Number(editingReceipt.receiptAmount || 0);
      const adjustmentAmountNum = Number(editingReceipt.adjustmentAmount || 0);
      const originalAmountNum = Number(editingReceipt.originalAmount || editingReceipt.totalAmount || 0);

      const newOutstanding = Math.max(0, originalAmountNum - receiptAmountNum - adjustmentAmountNum);

      const updatedTransactions = (editingReceipt.transactions || []).map(trans => {
        const invoiceAmount = Number(trans.invoiceAmount) || 0;
        const totalReceived = Number(trans.totalReceived || trans.receivedAmount) || 0;
        const adjustmentAmount = Number(trans.adjustmentAmount) || 0;
        const receivedAmount = totalReceived;
        
        return {
          ...trans,
          receivedAmount: receivedAmount.toFixed(2),
          totalReceived: totalReceived.toFixed(2)
        };
      });

      setFormData({
        ...editingReceipt,
        customerAddress: editingReceipt.customerAddress || '',
        customerCity: editingReceipt.customerCity || '',
        transactions: updatedTransactions,
        totalAmount: editingReceipt.totalAmount || '',
        netAmount: newOutstanding.toFixed(2),
        receiptAmount: receiptAmountNum.toFixed(2),
        adjustmentAmount: adjustmentAmountNum.toFixed(2),
      });
      setOriginalNetTotal(originalAmountNum);
      setCurrentOutstanding(newOutstanding);
      setReceiptAmountInput(receiptAmountNum.toFixed(2));

      const trans = editingReceipt.transactions || [];
      if (trans.length > 1) {
        setIsAllMR(true);
        setSelectedBill('');
      } else if (trans.length === 1) {
        setIsAllMR(false);
        setSelectedBill(trans[0].invoiceNo || '');
      } else {
        setIsAllMR(false);
        setSelectedBill('');
      }
    } else if (initialCustomer) {
      const maxVoucherNo = receipts.length > 0
        ? Math.max(...receipts.map(r => parseInt(r.voucherNo) || 0)) + 1
        : 1;
      setFormData({
        voucherNo: maxVoucherNo.toString(),
        voucherDate: new Date().toISOString().split('T')[0],
        customer: initialCustomer.customerName,
        customerAddress: initialCustomer.address || '',
        customerCity: initialCustomer.city || '',
        receiptType: '',
        receiptBy: '',
        bankName: '',
        chequeNo: '',
        chequeDate: '',
        remarks: '',
        transactions: [],
        totalAmount: '',
        netAmount: initialCustomer.obAmount || '0.00',
        receiptAmount: ''
      });
      setOriginalNetTotal(0);
      setCurrentOutstanding(Number(initialCustomer.obAmount || 0));
      setReceiptAmountInput('');
      setIsAllMR(true);
      setSelectedBill('');
    } else {
      const maxVoucherNo = receipts.length > 0
        ? Math.max(...receipts.map(r => parseInt(r.voucherNo) || 0)) + 1
        : 1;
      setFormData({
        voucherNo: maxVoucherNo.toString(),
        voucherDate: new Date().toISOString().split('T')[0],
        customer: '',
        customerAddress: '',
        customerCity: '',
        receiptType: '',
        receiptBy: '',
        bankName: '',
        chequeNo: '',
        chequeDate: '',
        remarks: '',
        transactions: [],
        totalAmount: '',
        netAmount: '',
        receiptAmount: ''
      });
      setOriginalNetTotal(0);
      setCurrentOutstanding(0);
      setReceiptAmountInput('');
      setIsAllMR(false);
      setSelectedBill('');
    }
  }, [editingReceipt, receipts, initialCustomer]);

  // Reset selected bill when customer changes
  useEffect(() => {
    setSelectedBill('');
  }, [formData.customer]);

  // Update transactions based on customer selection and bill selection
  useEffect(() => {
    if (editingReceipt || !formData.customer) return;

    let selectedSales = [];
    if (isAllMR) {
      selectedSales = filteredCustomerSales;
    } else if (selectedBill) {
      const sale = filteredCustomerSales.find(s => String(s.billNo) === String(selectedBill));
      if (sale) selectedSales = [sale];
    }

    const transactions = selectedSales.map((sale, index) => {
      const invoiceAmount = Number(sale.totals?.netAmount) || 0;
      const totalReceived = Number(sale.summary?.totalReceived) || 0;
      const receivedAmount = totalReceived;
      const balance = invoiceAmount - totalReceived;
      
      return {
        sno: index + 1,
        transactionType: 'Sales',
        remarks: `Sale Invoice ${sale.billNo}`,
        invoiceNo: sale.billNo,
        invoiceDate: sale.date || '',
        invoiceAmount: invoiceAmount.toFixed(2),
        receivedAmount: receivedAmount.toFixed(2),
        adjustmentAmount: '0.00',
        receiptAmount: '0.00',
        balance: balance.toFixed(2),
        saleId: sale.id,
        totalReceived: totalReceived.toFixed(2)
      };
    });

    setFormData(prev => ({ ...prev, transactions }));

    const totalOriginal = transactions.reduce((sum, t) => sum + Number(t.invoiceAmount || 0), 0);
    const totalOutstanding = transactions.reduce((sum, t) => sum + Number(t.balance || 0), 0);

    setOriginalNetTotal(totalOriginal);
    setCurrentOutstanding(totalOutstanding);
    setFormData(prev => ({
      ...prev,
      totalAmount: totalOriginal.toFixed(2),
      netAmount: totalOutstanding.toFixed(2)
    }));
  }, [filteredCustomerSales, isAllMR, selectedBill, formData.customer, editingReceipt]);

  // Update outstanding amount based on receipt and adjustment inputs
  useEffect(() => {
    const receiptAmount = Number(receiptAmountInput) || 0;
    const totalAdjustmentAmount = formData.transactions.reduce((total, trans) => {
      return total + (Number(trans.adjustmentAmount) || 0);
    }, 0);

    const baseOutstanding = formData.transactions.reduce((total, trans) => {
      const invoiceAmount = Number(trans.invoiceAmount) || 0;
      const totalReceived = Number(trans.totalReceived || trans.receivedAmount) || 0;
      return total + (invoiceAmount - totalReceived);
    }, 0);

    setCurrentOutstanding(Math.max(0, baseOutstanding));

    const remainingOutstanding = Math.max(0, baseOutstanding - receiptAmount - totalAdjustmentAmount);
    
    setFormData(prev => ({
      ...prev,
      netAmount: remainingOutstanding.toFixed(2),
      receiptAmount: receiptAmount.toFixed(2)
    }));
  }, [receiptAmountInput, formData.transactions]);

  // Handle customer selection
  const handleCustomerChange = (customerName) => {
    const selectedCustomer = customers.find(
      (c) => (c.customerName || c.name || '') === customerName
    );
    setFormData(prev => ({
      ...prev,
      customer: customerName,
      customerAddress:
        (selectedCustomer?.address1 || '') +
        (selectedCustomer?.address2 ? ' ' + selectedCustomer.address2 : '') ||
        selectedCustomer?.address ||
        '',
      customerCity: selectedCustomer?.city || ''
    }));
  };

  // Handle cheque number input
  const handleChequeNoChange = (e) => {
    const value = e.target.value;
    if (/^\d*$/.test(value)) {
      setFormData(prev => ({ ...prev, chequeNo: value }));
    }
  };

  // Handle bank name input
  const handleBankNameChange = (e) => {
    const value = e.target.value;
    if (/^[a-zA-Z\s&\-()]*$/.test(value)) {
      setFormData(prev => ({ ...prev, bankName: value }));
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!currentUser) {
      alert('You must be logged in to save a receipt.');
      return;
    }
    if (!formData.voucherNo || !formData.voucherDate || !formData.customer) {
      alert('Please fill in all required fields (Voucher No, Date, and Customer).');
      return;
    }
    if (formData.transactions.length === 0) {
      alert('Please ensure at least one transaction is populated.');
      return;
    }
    
    if (formData.receiptBy === 'CHQ') {
      if (!formData.bankName || !formData.chequeNo || !formData.chequeDate) {
        alert('Please fill in all required cheque fields (Bank Name, Cheque No, and Cheque Date).');
        return;
      }
    }
    
    const receiptAmount = Number(receiptAmountInput) || 0;
    const totalAdjustmentAmount = formData.transactions.reduce((total, trans) => {
      return total + (Number(trans.adjustmentAmount) || 0);
    }, 0);
    const newOutstanding = Math.max(0, currentOutstanding - receiptAmount - totalAdjustmentAmount);
    const paymentStatus = newOutstanding <= 0.01 ? 'Complete' : receiptAmount > 0 ? 'Partial' : 'Pending';
    
    try {
      const encodedEmail = encodeEmailForPath(currentUser.email);
      const receiptData = {
        ...formData,
        totalAmount: originalNetTotal.toFixed(2),
        netAmount: newOutstanding.toFixed(2),
        receiptAmount: receiptAmount.toFixed(2),
        adjustmentAmount: totalAdjustmentAmount.toFixed(2),
        originalAmount: originalNetTotal.toFixed(2),
        outstandingBefore: currentOutstanding.toFixed(2),
        timestamp: serverTimestamp(),
        createdBy: currentUser.email,
        paymentStatus
      };
      
      if (editingReceipt) {
        const oldTransactions = editingReceipt.transactions || [];
        const isAggregated = oldTransactions.length === 1 && Array.isArray(oldTransactions[0].saleIds);
        const oldReceiptAmount = Number(editingReceipt.receiptAmount) || 0;
        const oldAdjustmentTotal = Number(editingReceipt.adjustmentAmount) || 0;
        const oldCombined = oldReceiptAmount + oldAdjustmentTotal;

        if (isAggregated) {
          const oldSaleIds = oldTransactions[0].saleIds || [];
          const oldSalesData = [];
          for (const id of oldSaleIds) {
            const snap = await get(ref(database, `users/${encodedEmail}/sales/${id}`));
            if (snap.exists()) {
              oldSalesData.push({ id, data: snap.val() });
            }
          }
          const oldTotalSalesAmount = oldSalesData.reduce((sum, s) => sum + Number(s.data.totals?.netAmount || 0), 0);
          for (const oldSale of oldSalesData) {
            const oldProportion = oldTotalSalesAmount > 0 ? Number(oldSale.data.totals?.netAmount || 0) / oldTotalSalesAmount : 0;
            const oldSubtract = oldCombined * oldProportion;
            const currentReceived = Number(oldSale.data.summary?.totalReceived || 0);
            const newReceived = currentReceived - oldSubtract;
            const newOutstanding = Number(oldSale.data.totals?.netAmount || 0) - newReceived;
            const salePaymentStatus = newOutstanding <= 0.01 ? 'Complete' : newReceived > 0 ? 'Partial' : 'Pending';
            await update(ref(database, `users/${encodedEmail}/sales/${oldSale.id}`), {
              'summary/totalReceived': Math.max(0, newReceived).toFixed(2),
              'summary/outstanding': Math.max(0, newOutstanding).toFixed(2),
              'summary/paymentStatus': salePaymentStatus
            });
          }
        } else {
          const oldTotalInvoice = oldTransactions.reduce((sum, t) => sum + Number(t.invoiceAmount || 0), 0);
          for (const oldTrans of oldTransactions) {
            const oldSaleId = oldTrans.saleId;
            if (oldSaleId) {
              const snap = await get(ref(database, `users/${encodedEmail}/sales/${oldSaleId}`));
              if (snap.exists()) {
                const oldSale = { id: oldSaleId, data: snap.val() };
                const oldProportion = oldTotalInvoice > 0 ? Number(oldTrans.invoiceAmount || 0) / oldTotalInvoice : 0;
                const oldAdj = Number(oldTrans.adjustmentAmount || 0);
                const oldReceiptForThis = oldReceiptAmount * oldProportion;
                const oldSubtract = oldAdj + oldReceiptForThis;
                const currentReceived = Number(oldSale.data.summary?.totalReceived || 0);
                const newReceived = currentReceived - oldSubtract;
                const newOutstanding = Number(oldSale.data.totals?.netAmount || 0) - newReceived;
                const salePaymentStatus = newOutstanding <= 0.01 ? 'Complete' : newReceived > 0 ? 'Partial' : 'Pending';
                await update(ref(database, `users/${encodedEmail}/sales/${oldSale.id}`), {
                  'summary/totalReceived': Math.max(0, newReceived).toFixed(2),
                  'summary/outstanding': Math.max(0, newOutstanding).toFixed(2),
                  'summary/paymentStatus': salePaymentStatus
                });
              }
            }
          }
        }

        await set(ref(database, `users/${encodedEmail}/receipts/${editingReceipt.id}`), receiptData);
        alert('✅ Payment Updated Successfully!');
      } else {
        const newReceiptRef = push(ref(database, `users/${encodedEmail}/receipts`));
        await set(newReceiptRef, receiptData);
        alert('✅ Payment Receipt Saved Successfully!');
      }
      
      const newTotalInvoice = formData.transactions.reduce((sum, t) => sum + Number(t.invoiceAmount || 0), 0);
      const newReceiptAmount = receiptAmount;
      for (const newTrans of formData.transactions) {
        const newSaleId = newTrans.saleId;
        if (newSaleId) {
          const snap = await get(ref(database, `users/${encodedEmail}/sales/${newSaleId}`));
          if (snap.exists()) {
            const newSale = { id: newSaleId, data: snap.val() };
            const newProportion = newTotalInvoice > 0 ? Number(newTrans.invoiceAmount || 0) / newTotalInvoice : 0;
            const newAdj = Number(newTrans.adjustmentAmount || 0);
            const newReceiptForThis = newReceiptAmount * newProportion;
            const newAdd = newAdj + newReceiptForThis;
            const currentReceived = Number(newSale.data.summary?.totalReceived || 0);
            const newReceived = currentReceived + newAdd;
            const newOutstanding = Number(newSale.data.totals?.netAmount || 0) - newReceived;
            const salePaymentStatus = newOutstanding <= 0.01 ? 'Complete' : newReceived > 0 ? 'Partial' : 'Pending';
            await update(ref(database, `users/${encodedEmail}/sales/${newSale.id}`), {
              'summary/netAmt': Number(newSale.data.totals?.netAmount || 0).toFixed(2),
              'summary/outstanding': Math.max(0, newOutstanding).toFixed(2),
              'summary/paymentStatus': salePaymentStatus,
              'summary/lastPaymentDate': new Date().toISOString(),
              'summary/totalReceived': newReceived.toFixed(2)
            });
          }
        }
      }
      
      handleCancel();
      if (onBack) onBack();
    } catch (error) {
      console.error("Error saving receipt:", error);
      alert(`Error saving receipt: ${error.message}`);
    }
  };

  // Handle form cancellation
  const handleCancel = () => {
    setShowAddForm(false);
    setEditingReceipt(null);
    setFormData({
      voucherNo: receipts.length > 0
        ? (Math.max(...receipts.map(r => parseInt(r.voucherNo) || 0)) + 1).toString()
        : '1',
      voucherDate: new Date().toISOString().split('T')[0],
      customer: initialCustomer?.customerName || '',
      customerAddress: initialCustomer?.address || '',
      customerCity: initialCustomer?.city || '',
      receiptType: '',
      receiptBy: '',
      bankName: '',
      chequeNo: '',
      chequeDate: '',
      remarks: '',
      transactions: [],
      totalAmount: '',
      netAmount: initialCustomer?.obAmount || '0.00',
      receiptAmount: ''
    });
    setOriginalNetTotal(0);
    setCurrentOutstanding(Number(initialCustomer?.obAmount || 0));
    setReceiptAmountInput('');
    if (onBack) onBack();
  };

  // Handle edit receipt
  const handleEdit = (receipt) => {
    setEditingReceipt(receipt);
    setShowAddForm(true);
  };

  // Handle delete receipt
  const handleDelete = async (id) => {
    if (!currentUser) return;
    if (!window.confirm('Are you sure you want to delete this receipt? This action cannot be undone.')) {
      return;
    }
    try {
      const encodedEmail = encodeEmailForPath(currentUser.email);
      const receiptRef = ref(database, `users/${encodedEmail}/receipts/${id}`);
      const receiptSnapshot = await get(receiptRef);
      const receiptData = receiptSnapshot.val();
      
      if (receiptData) {
        const transactions = receiptData.transactions || [];
        const isAggregated = transactions.length === 1 && Array.isArray(transactions[0].saleIds);
        const receiptAmount = Number(receiptData.receiptAmount || 0);
        const adjustmentTotal = Number(receiptData.adjustmentAmount || 0);
        const combined = receiptAmount + adjustmentTotal;

        if (isAggregated) {
          const saleIds = transactions[0].saleIds || [];
          const salesData = [];
          for (const saleId of saleIds) {
            const saleRef = ref(database, `users/${encodedEmail}/sales/${saleId}`);
            const saleSnapshot = await get(saleRef);
            if (saleSnapshot.exists()) {
              salesData.push({ id: saleId, data: saleSnapshot.val() });
            }
          }
          const totalSalesAmount = salesData.reduce((total, sale) => {
            return total + (sale.data.totals?.netAmount || 0);
          }, 0);
          for (const sale of salesData) {
            const proportion = totalSalesAmount > 0 ? (sale.data.totals?.netAmount || 0) / totalSalesAmount : 0;
            const subtract = combined * proportion;
            const currentTotalReceived = Number(sale.data.summary?.totalReceived || 0);
            const currentOutstanding = Number(sale.data.summary?.outstanding || sale.data.totals?.netAmount || 0);
            const newTotalReceived = currentTotalReceived - subtract;
            const newOutstanding = currentOutstanding + subtract;
            const paymentStatus = newOutstanding <= 0.01 ? 'Complete' : newTotalReceived > 0 ? 'Partial' : 'Pending';
            await update(ref(database, `users/${encodedEmail}/sales/${sale.id}`), {
              'summary/totalReceived': Math.max(0, newTotalReceived).toFixed(2),
              'summary/outstanding': newOutstanding.toFixed(2),
              'summary/paymentStatus': paymentStatus,
              'summary/lastPaymentDate': new Date().toISOString(),
            });
          }
        } else {
          const totalInvoice = transactions.reduce((total, trans) => {
            return total + (parseFloat(trans.invoiceAmount) || 0);
          }, 0);
          for (const trans of transactions) {
            const saleId = trans.saleId;
            if (saleId) {
              const saleRef = ref(database, `users/${encodedEmail}/sales/${saleId}`);
              const saleSnapshot = await get(saleRef);
              if (saleSnapshot.exists()) {
                const sale = { id: saleId, data: saleSnapshot.val() };
                const proportion = totalInvoice > 0 ? (parseFloat(trans.invoiceAmount) || 0) / totalInvoice : 0;
                const adj = parseFloat(trans.adjustmentAmount) || 0;
                const receiptForThis = receiptAmount * proportion;
                const subtract = adj + receiptForThis;
                const currentTotalReceived = Number(sale.data.summary?.totalReceived || 0);
                const currentOutstanding = Number(sale.data.summary?.outstanding || sale.data.totals?.netAmount || 0);
                const newTotalReceived = currentTotalReceived - subtract;
                const newOutstanding = currentOutstanding + subtract;
                const paymentStatus = newOutstanding <= 0.01 ? 'Complete' : newTotalReceived > 0 ? 'Partial' : 'Pending';
                await update(ref(database, `users/${encodedEmail}/sales/${sale.id}`), {
                  'summary/totalReceived': Math.max(0, newTotalReceived).toFixed(2),
                  'summary/outstanding': newOutstanding.toFixed(2),
                  'summary/paymentStatus': paymentStatus,
                  'summary/lastPaymentDate': new Date().toISOString(),
                });
              }
            }
          }
        }
        
        await remove(receiptRef);
        alert(`Receipt with Voucher No "${receiptData.voucherNo}" deleted successfully!`);
      } else {
        console.warn("Receipt data not found for id:", id);
        alert('Receipt not found.');
      }
    } catch (error) {
      console.error("Error deleting receipt:", error, { receiptId: id });
      alert(`Error deleting receipt: ${error.message}`);
    }
  };

  // Handle receipt amount input
  const handleReceiptChange = (e) => {
    const value = e.target.value;
    setReceiptAmountInput(value);
  };

  return (
    <div style={styles.container}>
      {!currentUser ? (
        <div style={styles.noData}>
          <div style={{ fontSize: '4rem', marginBottom: '1rem', opacity: '0.5' }}>🔐</div>
          <h3 style={{ color: '#4a5568', marginBottom: '0.5rem' }}>Receipt Management System</h3>
          <p>Please log in to access the Receipt Management System.</p>
        </div>
      ) : !showAddForm ? (
        <div style={styles.formWrapper1}>
          <div style={styles.header}>
            <h1 style={styles.headerTitle}>Receipt Management System</h1>
            <div style={styles.headerButtons}>
              <button
                onClick={() => setShowAddForm(true)}
                style={styles.addButton}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 20px rgba(37, 99, 235, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 15px rgba(37, 99, 235, 0.3)';
                }}
              >
                + Add Receipt
              </button>
            </div>
          </div>
          <div style={styles.tableContainerList}>
            {receipts.length === 0 ? (
              <div style={styles.noData}>
                <div style={{ fontSize: '4rem', marginBottom: '1rem', opacity: '0.5' }}>📄</div>
                <h3 style={{ color: '#4a5568', marginBottom: '0.5rem' }}>No Receipts Found</h3>
                <p style={{ marginBottom: '1.5rem' }}>Get started by adding your first receipt!</p>
                <button
                  onClick={() => setShowAddForm(true)}
                  style={{
                    ...styles.addButton,
                    fontSize: '1rem',
                    padding: '1rem 2rem',
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 20px rgba(37, 99, 235, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 15px rgba(37, 99, 235, 0.3)';
                  }}
                >
                  + Add Your First Receipt
                </button>
              </div>
            ) : (
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={{ ...styles.tableHeader, ...styles.srNoCell }}>Sr.No</th>
                    <th style={styles.tableHeader}>Voucher No</th>
                    <th style={styles.tableHeader}>Date</th>
                    <th style={styles.tableHeader}>Customer</th>
                    <th style={styles.tableHeader}>Receipt By</th>
                    <th style={styles.tableHeader}>Original Amount</th>
                    <th style={styles.tableHeader}>Payment Amount</th>
                    <th style={styles.tableHeader}>Outstanding</th>
                    <th style={styles.tableHeader}>Status</th>
                    <th style={styles.tableHeader}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {receipts.map((receipt, index) => {
                    const originalAmount = Number(receipt.originalAmount || receipt.totalAmount || 0);
                    const paymentAmount = Number(receipt.receiptAmount || 0);
                    const outstandingAmount = Number(receipt.netAmount || 0);
                    let paymentStatus = 'Pending';
                    if (outstandingAmount <= 0.01) {
                      paymentStatus = 'Complete';
                    } else if (paymentAmount > 0 && outstandingAmount < originalAmount) {
                      paymentStatus = 'Partial';
                    }

                    return (
                      <tr
                        key={receipt.id}
                        style={{
                          backgroundColor: index % 2 === 0 ? '#f7fafc' : 'white',
                          transition: 'background-color 0.2s ease',
                        }}
                      >
                        <td style={{ ...styles.tableCell, ...styles.srNoCell }}>{index + 1}</td>
                        <td style={styles.tableCell}>
                          <span style={{ fontWeight: '600' }}>{receipt.voucherNo || '-'}</span>
                        </td>
                        <td style={styles.tableCell}>{formatDate(receipt.voucherDate)}</td>
                        <td style={styles.tableCell}>
                          <div style={{ textAlign: 'left' }}>
                            <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>
                              {receipt.customer || '-'}
                            </div>
                            {receipt.customerAddress && (
                              <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                {receipt.customerAddress}
                              </div>
                            )}
                          </div>
                        </td>
                        <td style={styles.tableCell}>
                          <span style={{ 
                            padding: '0.25rem 0.5rem', 
                            backgroundColor: '#f3f4f6', 
                            borderRadius: '4px',
                            fontSize: '0.8rem'
                          }}>
                            {receipt.receiptBy || '-'}
                          </span>
                        </td>
                        <td style={styles.tableCell}>
                          <span style={{ fontWeight: '600' }}>₹{formatCurrency(originalAmount)}</span>
                        </td>
                        <td style={styles.tableCell}>
                          <span style={{ fontWeight: '600', color: '#10b981' }}>₹{formatCurrency(paymentAmount)}</span>
                        </td>
                        <td style={styles.tableCell}>
                          <span style={{ 
                            fontWeight: '600', 
                            color: outstandingAmount > 0 ? '#ef4444' : '#10b981' 
                          }}>
                            ₹{formatCurrency(outstandingAmount)}
                          </span>
                        </td>
                        <td style={styles.tableCell}>
                          <span 
                            style={{
                              ...styles.paymentStatusBadge,
                              backgroundColor: getPaymentStatusColor(paymentStatus)
                            }}
                          >
                            {getPaymentStatusText(paymentStatus)}
                          </span>
                        </td>
                        <td style={{ ...styles.tableCell, ...styles.actionCell }}>
                          <button
                            onClick={() => handleEdit(receipt)}
                            style={styles.editButton}
                            title="Edit Receipt"
                            onMouseEnter={(e) => {
                              e.target.style.transform = 'translateY(-1px)';
                              e.target.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.4)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.transform = 'translateY(0)';
                              e.target.style.boxShadow = '0 2px 8px rgba(16, 185, 129, 0.3)';
                            }}
                          >
                            ✏️
                          </button>
                          
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      ) : (
        <div style={styles.formWrapper}>
          <div style={styles.header}>
            <div style={styles.headerTitle}>
              {editingReceipt ? 'Edit Receipt' : 'Add Receipt'}
            </div>
            <div style={styles.headerButtons}>
              <button
                onClick={handleCancel}
                style={styles.backButton}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = '0 4px 12px rgba(107, 114, 128, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 2px 8px rgba(107, 114, 128, 0.3)';
                }}
              >
                ← Back
              </button>
            </div>
          </div>
          <div style={styles.section}>
            <h2 style={styles.sectionHeading}>📋 Receipt Details</h2>
            <div style={styles.gridContainer}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Voucher No*</label>
                <input
                  type="text"
                  value={formData.voucherNo}
                  readOnly
                  style={{ ...styles.input, backgroundColor: '#f3f4f6' }}
                  required
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Date*</label>
                <input
                  type="date"
                  value={formData.voucherDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, voucherDate: e.target.value }))}
                  style={styles.input}
                  required
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>All MR</label>
                <input
                  type="checkbox"
                  checked={isAllMR}
                  onChange={(e) => setIsAllMR(e.target.checked)}
                  style={{ cursor: 'pointer' }}
                />
              </div>
              {!isAllMR && (
                <div style={styles.formGroup}>
                  <label style={styles.label}>Bill Number</label>
                  <select
                    value={selectedBill}
                    onChange={(e) => setSelectedBill(e.target.value)}
                    style={styles.select}
                  >
                    <option value="">Select Bill Number</option>
                    {filteredCustomerSales.map(sale => (
                      <option key={sale.id} value={sale.billNo}>
                        {sale.billNo}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div style={styles.formGroup}>
                <label style={styles.label}>Customer*</label>
                <select
                  value={formData.customer}
                  onChange={(e) => handleCustomerChange(e.target.value)}
                  style={styles.select}
                  required
                >
                  <option value="">Select Customer</option>
                  {customers.map(customer => (
                    <option
                      key={customer.id}
                      value={customer.customerName || customer.name || ''}
                    >
                      {customer.customerName || customer.name || ''}
                    </option>
                  ))}
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Customer Address</label>
                <input
                  type="text"
                  value={formData.customerAddress}
                  readOnly
                  style={{ ...styles.input, backgroundColor: '#f3f4f6' }}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Customer City</label>
                <input
                  type="text"
                  value={formData.customerCity}
                  readOnly
                  style={{ ...styles.input, backgroundColor: '#f3f4f6' }}
                />
              </div>
            </div>
          </div>
          {formData.transactions.length > 0 && (
            <div style={styles.section}>
              <h2 style={styles.sectionHeading}>📦 Transactions</h2>
              <div style={styles.tableContainer}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={{ ...styles.tableHeader, width: '60px' }}>S.No</th>
                      <th style={{ ...styles.tableHeader, width: '100px' }}>Type</th>
                      <th style={{ ...styles.tableHeader, width: '150px' }}>Remarks</th>
                      <th style={{ ...styles.tableHeader, width: '100px' }}>Inv No</th>
                      <th style={{ ...styles.tableHeader, width: '120px' }}>Inv Date</th>
                      <th style={styles.tableHeader}>Inv Amt</th>
                      <th style={styles.tableHeader}>Received Amt</th>
                      <th style={styles.tableHeader}>Adj Amt</th>
                      <th style={styles.tableHeader}>Receipt Amt</th>
                      <th style={styles.tableHeader}>Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.transactions.map((trans, index) => (
                      <tr
                        key={trans.sno}
                        style={{
                          backgroundColor: index % 2 === 0 ? '#f7fafc' : 'white',
                          transition: 'background-color 0.2s ease',
                        }}
                      >
                        <td style={{ ...styles.tableCell, fontWeight: '600', color: '#2563eb', width: '60px' }}>
                          {trans.sno}
                        </td>
                        <td style={styles.tableCell}>{trans.transactionType}</td>
                        <td style={styles.tableCell}>
                          <input
                            type="text"
                            value={trans.remarks}
                            onChange={(e) => {
                              const newTransactions = [...formData.transactions];
                              newTransactions[index].remarks = e.target.value;
                              setFormData(prev => ({ ...prev, transactions: newTransactions }));
                            }}
                            style={styles.tableInput}
                          />
                        </td>
                        <td style={styles.tableCell}>{trans.invoiceNo}</td>
                        <td style={styles.tableCell}>
                          {trans.invoiceDate || '-'}
                        </td>
                        <td style={{ ...styles.tableCell, textAlign: 'right' }}>
                          ₹{(parseFloat(trans.invoiceAmount) || 0).toFixed(2)}
                        </td>
                        <td style={{ ...styles.tableCell, textAlign: 'right' }}>
                          ₹{(parseFloat(trans.receivedAmount) || 0).toFixed(2)}
                        </td>
                        <td style={styles.tableCell}>
                          <input
                            type="number"
                            value={trans.adjustmentAmount}
                            onChange={(e) => {
                              const newTransactions = [...formData.transactions];
                              newTransactions[index].adjustmentAmount = e.target.value;
                              const invoiceAmount = parseFloat(newTransactions[index].invoiceAmount) || 0;
                              const totalReceived = parseFloat(newTransactions[index].totalReceived || newTransactions[index].receivedAmount) || 0;
                              const adjustmentAmount = parseFloat(newTransactions[index].adjustmentAmount) || 0;
                              newTransactions[index].balance = (
                                invoiceAmount - totalReceived - adjustmentAmount
                              ).toFixed(2);
                              setFormData(prev => ({ ...prev, transactions: newTransactions }));
                            }}
                            style={styles.tableInput}
                          />
                        </td>
                        <td style={{ ...styles.tableCell, fontWeight: '600', color: '#10b981', textAlign: 'right' }}>
                          ₹{(parseFloat(trans.receiptAmount) || 0).toFixed(2)}
                        </td>
                        <td style={{ ...styles.tableCell, textAlign: 'right' }}>
                          ₹{(parseFloat(trans.balance) || 0).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <div style={styles.section}>
            <h2 style={styles.sectionHeading}>💳 Receipt Details</h2>
            <div style={styles.gridContainer}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Receipt Type</label>
                <select
                  value={formData.receiptType}
                  onChange={(e) => setFormData(prev => ({ ...prev, receiptType: e.target.value }))}
                  style={styles.select}
                >
                  <option value="">Select Receipt Type</option>
                  <option value="Bill">Bill</option>
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Receipt By</label>
                <select
                  value={formData.receiptBy}
                  onChange={(e) => setFormData(prev => ({ ...prev, receiptBy: e.target.value }))}
                  style={styles.select}
                >
                  <option value="">Select Receipt Method</option>
                  {paymentModes.map((mode) => (
                    <option key={mode.id} value={mode.code}>
                      {mode.code}
                    </option>
                  ))}
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Bank Name{formData.receiptBy === 'CHQ' ? ' *' : ''}
                </label>
                <input
                  type="text"
                  value={formData.bankName}
                  onChange={handleBankNameChange}
                  style={styles.input}
                  placeholder="Enter bank name (letters only)"
                  required={formData.receiptBy === 'CHQ'}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Cheque No.{formData.receiptBy === 'CHQ' ? ' *' : ''}
                </label>
                <input
                  type="text"
                  value={formData.chequeNo}
                  onChange={handleChequeNoChange}
                  style={styles.input}
                  placeholder="Enter numbers only"
                  required={formData.receiptBy === 'CHQ'}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Cheque Date{formData.receiptBy === 'CHQ' ? ' *' : ''}
                </label>
                <input
                  type="date"
                  value={formData.chequeDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, chequeDate: e.target.value }))}
                  style={styles.input}
                  required={formData.receiptBy === 'CHQ'}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Remarks</label>
                <textarea
                  value={formData.remarks}
                  onChange={(e) => setFormData(prev => ({ ...prev, remarks: e.target.value }))}
                  style={styles.textarea}
                />
              </div>
            </div>
          </div>
          <div style={styles.section}>
            <h2 style={styles.sectionHeading}>📊 Summary</h2>
            <div style={styles.summaryContainer}>
              <div style={styles.summaryBox}>
                <div style={styles.summaryRow}>
                  <span>Original Amount</span>
                  <input
                    type="number"
                    value={originalNetTotal.toFixed(2)}
                    readOnly
                    style={styles.summaryInput}
                  />
                </div>
                <div style={styles.summaryRow}>
                  <span>Current Outstanding</span>
                  <input
                    type="number"
                    value={currentOutstanding.toFixed(2)}
                    readOnly
                    style={styles.summaryInput}
                  />
                </div>
                <div style={styles.summaryRow}>
                  <span>Receipt Amount</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max={currentOutstanding}
                    value={receiptAmountInput}
                    onChange={handleReceiptChange}
                    style={{ ...styles.summaryInput, backgroundColor: '#fff' }}
                    placeholder="0.00"
                  />
                </div>
                <div style={styles.summaryRow}>
                  <span>Adjustment Amount</span>
                  <input
                    type="number"
                    value={formData.transactions.reduce((total, trans) => {
                      return total + (Number(trans.adjustmentAmount) || 0);
                    }, 0).toFixed(2)}
                    readOnly
                    style={styles.summaryInput}
                  />
                </div>
                <div style={styles.summaryTotal}>
                  <span>Remaining Outstanding</span>
                  <span>₹{formData.netAmount}</span>
                </div>
              </div>
            </div>
          </div>
          <div style={styles.actionButtons}>
            <button
              onClick={handleSubmit}
              style={styles.saveButton}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 2px 8px rgba(16, 185, 129, 0.3)';
              }}
            >
              ✅ {editingReceipt ? 'Update' : 'Save'}
            </button>
            <button
              onClick={handleCancel}
              style={styles.cancelButton}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 4px 12px rgba(220, 38, 38, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 2px 8px rgba(220, 38, 38, 0.3)';
              }}
            >
              ❌ Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReceiptManagementSystem;